import Add from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  WasteInvoiceImportVendor,
  WasteInvoiceStatus,
  WasteInvoicesTableQuery,
  WasteInvoicesTableQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";

import { WASTE_INVOICES_QUERY } from "../queries";
import { formatInvoiceDate, formatInvoiceMoney, formatServicePeriod } from "../utils";

type InvoiceRow = WasteInvoicesTableQuery["wasteInvoices"]["items"][number];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT: GridSortModel = [{ field: "invoiceDate", sort: "desc" }];
const INITIAL_VISIBILITY = { createdAt: false };
const INVOICE_VENDOR_FILTER_VALUES = {
  [WasteInvoiceImportVendor.CleanEarth]: "Clean Earth",
  [WasteInvoiceImportVendor.Republic]: "Republic",
} satisfies Record<WasteInvoiceImportVendor, string>;
const INVOICE_STATUS_LABELS = {
  [WasteInvoiceStatus.PendingReview]: "Pending review",
  [WasteInvoiceStatus.Reviewed]: "Reviewed",
  [WasteInvoiceStatus.Closed]: "Closed",
} satisfies Record<WasteInvoiceStatus, string>;

const FILTER_KEYS: FilterKey[] = [
  { key: "invoiceNumber", header: "Invoice #" },
  {
    key: "vendor",
    header: "Vendor",
    filterKeyType: "enum",
    enumValues: Object.values(INVOICE_VENDOR_FILTER_VALUES),
  },
  { key: "invoiceDate", header: "Invoice date", filterKeyType: "date" },
  { key: "servicePeriod", header: "Service period", filterKeyType: "date" },
  { key: "invoiceTotal", header: "Total", filterKeyType: "number" },
];
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Invoice",
    fields: ["invoiceNumber", "vendor", "invoiceDate", "servicePeriod", "invoiceTotal"],
  },
];

const INVOICE_COLUMNS: OmnisearchGridColDef<InvoiceRow>[] = [
  {
    field: "invoiceNumber",
    headerName: "Invoice #",
    minWidth: 150,
    flex: 0.8,
    filterable: false,
  },
  {
    field: "vendor",
    headerName: "Vendor",
    minWidth: 170,
    flex: 1,
    filterable: false,
    valueGetter: (_, row) => row.vendor.name,
  },
  {
    field: "invoiceDate",
    headerName: "Invoice date",
    minWidth: 125,
    flex: 0.65,
    filterable: false,
    valueGetter: (_, row) => formatInvoiceDate(row.invoiceDate),
  },
  {
    field: "servicePeriodStart",
    headerName: "Service period",
    minWidth: 205,
    flex: 1,
    filterable: false,
    valueGetter: (_, row) => formatServicePeriod(row.servicePeriodStart, row.servicePeriodEnd),
  },
  {
    field: "invoiceTotal",
    headerName: "Total",
    minWidth: 130,
    flex: 0.65,
    type: "number",
    filterable: false,
    valueGetter: (_, row) => formatInvoiceMoney(row.invoiceTotal),
  },
  {
    field: "reviewStatus",
    headerName: "Review status",
    minWidth: 145,
    flex: 0.7,
    filterable: false,
    valueGetter: (_, row) => INVOICE_STATUS_LABELS[row.reviewStatus],
  },
  {
    field: "lineCount",
    headerName: "Lines",
    minWidth: 90,
    flex: 0.4,
    type: "number",
    filterable: false,
  },
  {
    field: "createdAt",
    headerName: "Imported",
    minWidth: 125,
    flex: 0.65,
    filterable: false,
    valueGetter: (_, row) => formatInvoiceDate(row.createdAt),
  },
];

const getInvoiceItems = (data: WasteInvoicesTableQuery) => data.wasteInvoices.items;
const getInvoiceCount = (data: WasteInvoicesTableQuery) => data.wasteInvoices.count;
const getInvoiceRowLink = (row: InvoiceRow) => `./${row.id}`;

export function InvoicesTable({
  tenantId,
  canImport,
  onImport,
}: {
  tenantId: string;
  canImport: boolean;
  onImport: () => void;
}) {
  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): WasteInvoicesTableQueryVariables => ({
      tenantId,
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((item) => ({ field: item.field, sort: item.sort })),
    }),
    [tenantId]
  );

  const commandButtons = useMemo(
    () =>
      canImport
        ? [
            <Button
              key="import-invoices"
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={onImport}
            >
              Import invoices
            </Button>,
          ]
        : [],
    [canImport, onImport]
  );

  return (
    <OmnisearchDataGridV2
      persistenceKey="waste-invoices-table"
      tenantId={tenantId}
      query={WASTE_INVOICES_QUERY}
      getItems={getInvoiceItems}
      getCount={getInvoiceCount}
      buildVariables={buildVariables}
      columns={INVOICE_COLUMNS}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialPageSize={25}
      initialSortModel={INITIAL_SORT}
      initialColumnVisibilityModel={INITIAL_VISIBILITY}
      additionalFilterKeys={FILTER_KEYS}
      filterGroups={FILTER_GROUPS}
      commandButtons={commandButtons}
      getRowLink={getInvoiceRowLink}
      disableRowSelectionOnClick
      noRowsMessage="No waste invoices have been imported for this organization."
      noDataButtonText={canImport ? "Import invoices" : undefined}
      onNoDataButtonClick={canImport ? onImport : undefined}
    />
  );
}
